import { randomUuid } from './messengerV2Platform.js';

export const MESSENGER_V2_IMAGE_EDIT_VERSION = 1;
export const MESSENGER_V2_IMAGE_TEXT_MAX_WIDTH = 0.9;
export const MESSENGER_V2_IMAGE_LAYER_SCALE_LIMITS = Object.freeze({
  text: Object.freeze({ minimum: 0.5, maximum: 3 }),
  emoji: Object.freeze({ minimum: 0.4, maximum: 3 }),
  default: Object.freeze({ minimum: 0.2, maximum: 5 }),
});
export const MESSENGER_V2_IMAGE_EDIT_COLOR_OPTIONS = Object.freeze([
  Object.freeze({
    id: 'foreground',
    label: 'Foreground',
    value: 'var(--image-editor-palette-foreground)',
  }),
  Object.freeze({
    id: 'surface',
    label: 'Surface',
    value: 'var(--image-editor-palette-surface)',
  }),
  Object.freeze({
    id: 'danger',
    label: 'Danger',
    value: 'var(--image-editor-palette-danger)',
  }),
  Object.freeze({
    id: 'warning',
    label: 'Warning',
    value: 'var(--image-editor-palette-warning)',
  }),
  Object.freeze({
    id: 'success',
    label: 'Success',
    value: 'var(--image-editor-palette-success)',
  }),
  Object.freeze({
    id: 'aqua',
    label: 'Aqua',
    value: 'var(--image-editor-palette-aqua)',
  }),
  Object.freeze({
    id: 'info',
    label: 'Info',
    value: 'var(--image-editor-palette-info)',
  }),
  Object.freeze({
    id: 'violet',
    label: 'Violet',
    value: 'var(--image-editor-palette-violet)',
  }),
  Object.freeze({
    id: 'rose',
    label: 'Rose',
    value: 'var(--image-editor-palette-rose)',
  }),
]);
export const DEFAULT_MESSENGER_V2_IMAGE_EDIT_COLOR = MESSENGER_V2_IMAGE_EDIT_COLOR_OPTIONS[0].value;
export const DEFAULT_MESSENGER_V2_IMAGE_EDIT_BRUSH_COLOR =
  MESSENGER_V2_IMAGE_EDIT_COLOR_OPTIONS[4].value;
export const DEFAULT_MESSENGER_V2_IMAGE_TEXT_BACKGROUND = 'transparent';

const LEGACY_MESSENGER_V2_IMAGE_TEXT_EDITOR_BACKGROUND =
  'var(--image-editor-text-background)';

const clamp = (value, minimum, maximum) => Math.min(maximum, Math.max(minimum, Number(value) || 0));

export const resolveMessengerV2ImageLayerPixelSize = (layer, imageHeight) => {
  const defaultSize = layer?.type === 'emoji' ? 0.16 : 0.075;
  const normalizedSize = clamp(layer?.size || defaultSize, 0.001, 1);
  const scale = Math.max(0.01, Number(layer?.scale) || 1);
  return Math.max(1, normalizedSize * Math.max(1, Number(imageHeight) || 1) * scale);
};

const createPresent = () => ({
  crop: {
    position: { x: 0, y: 0 },
    zoom: 1,
    rotation: 0,
    area: { x: 0, y: 0, width: 100, height: 100 },
  },
  layers: [],
  brush: {
    color: DEFAULT_MESSENGER_V2_IMAGE_EDIT_BRUSH_COLOR,
    width: 0.012,
  },
});

export const createMessengerV2ImageEditDocument = () => ({
  version: MESSENGER_V2_IMAGE_EDIT_VERSION,
  past: [],
  present: createPresent(),
  future: [],
  session: null,
});

const normalizeLayers = (layers) => {
  const layerIds = new Set();
  const normalizedLayers = [];
  layers.forEach((layer) => {
    const layerId = String(layer?.id || '');
    if (!layerId || layerIds.has(layerId)) return;
    layerIds.add(layerId);
    normalizedLayers.push(
      layer?.type === 'text' &&
        layer.background === LEGACY_MESSENGER_V2_IMAGE_TEXT_EDITOR_BACKGROUND
        ? {
            ...layer,
            background: DEFAULT_MESSENGER_V2_IMAGE_TEXT_BACKGROUND,
          }
        : layer,
    );
  });
  return normalizedLayers;
};

export const resolveMessengerV2ImageEditDocument = (document = null) => {
  if (
    document?.version !== MESSENGER_V2_IMAGE_EDIT_VERSION ||
    !document?.present ||
    !Array.isArray(document.present.layers)
  ) {
    return createMessengerV2ImageEditDocument();
  }
  const layers = normalizeLayers(document.present.layers);
  const layersChanged =
    layers.length !== document.present.layers.length ||
    layers.some((layer, index) => layer !== document.present.layers[index]);
  return !layersChanged
    ? document
    : {
        ...document,
        present: {
          ...document.present,
          layers,
        },
      };
};

export const beginMessengerV2ImageToolSession = (document, tool, layerId = null) => {
  const current = resolveMessengerV2ImageEditDocument(document);
  if (current.session) return current;
  return {
    ...current,
    session: {
      tool,
      layerId,
      past: current.past,
      present: current.present,
      future: current.future,
    },
  };
};

export const setMessengerV2ImageToolSessionLayer = (document, layerId) => {
  const current = resolveMessengerV2ImageEditDocument(document);
  if (!current.session || String(current.session.layerId || '') === String(layerId || '')) {
    return current;
  }
  return {
    ...current,
    session: {
      ...current.session,
      layerId,
    },
  };
};

export const prepareMessengerV2ImageTextSession = (document) => {
  const current = beginMessengerV2ImageToolSession(document, 'text');
  const layerId =
    current.session?.tool === 'text' && current.session.layerId
      ? current.session.layerId
      : randomUuid();
  return {
    document: setMessengerV2ImageToolSessionLayer(current, layerId),
    layerId,
  };
};

export const commitMessengerV2ImageToolSession = (document) => {
  const current = resolveMessengerV2ImageEditDocument(document);
  if (!current.session) return current;
  return {
    ...current,
    past:
      current.present === current.session.present
        ? current.session.past
        : [...current.session.past, current.session.present].slice(-50),
    future: current.present === current.session.present ? current.session.future : [],
    session: null,
  };
};

export const cancelMessengerV2ImageToolSession = (document) => {
  const current = resolveMessengerV2ImageEditDocument(document);
  if (!current.session) return current;
  return {
    ...current,
    past: current.session.past,
    present: current.session.present,
    future: current.session.future,
    session: null,
  };
};

const commitPresent = (document, nextPresent) => {
  const current = resolveMessengerV2ImageEditDocument(document);
  if (Object.is(current.present, nextPresent)) return current;
  return {
    ...current,
    past: [...current.past, current.present].slice(-50),
    present: nextPresent,
    future: [],
  };
};

export const updateMessengerV2ImageEditDocument = (document, updater) => {
  const current = resolveMessengerV2ImageEditDocument(document);
  const nextPresent = typeof updater === 'function' ? updater(current.present) : updater;
  return nextPresent && nextPresent !== current.present
    ? commitPresent(current, nextPresent)
    : current;
};

export const checkpointMessengerV2ImageEdit = (document) => {
  const current = resolveMessengerV2ImageEditDocument(document);
  return {
    ...current,
    past: [...current.past, current.present].slice(-50),
    future: [],
  };
};

export const replaceMessengerV2ImageEditPresent = (document, updater) => {
  const current = resolveMessengerV2ImageEditDocument(document);
  const nextPresent = typeof updater === 'function' ? updater(current.present) : updater;
  return nextPresent && nextPresent !== current.present
    ? { ...current, present: nextPresent }
    : current;
};

export const undoMessengerV2ImageEdit = (document) => {
  const current = resolveMessengerV2ImageEditDocument(document);
  if (!current.past.length) return current;
  const previous = current.past[current.past.length - 1];
  return {
    ...current,
    past: current.past.slice(0, -1),
    present: previous,
    future: [current.present, ...current.future].slice(0, 50),
  };
};

export const redoMessengerV2ImageEdit = (document) => {
  const current = resolveMessengerV2ImageEditDocument(document);
  if (!current.future.length) return current;
  const next = current.future[0];
  return {
    ...current,
    past: [...current.past, current.present].slice(-50),
    present: next,
    future: current.future.slice(1),
  };
};

export const setMessengerV2ImageCrop = (document, crop) =>
  updateMessengerV2ImageEditDocument(document, (present) => ({
    ...present,
    crop: {
      position: {
        x: Number(crop?.position?.x) || 0,
        y: Number(crop?.position?.y) || 0,
      },
      zoom: clamp(crop?.zoom || 1, 1, 8),
      rotation: (((Number(crop?.rotation) || 0) % 360) + 360) % 360,
      area: {
        x: clamp(crop?.area?.x, 0, 100),
        y: clamp(crop?.area?.y, 0, 100),
        width: clamp(crop?.area?.width || 100, 0.1, 100),
        height: clamp(crop?.area?.height || 100, 0.1, 100),
      },
    },
  }));

const createLayerBase = (type) => ({
  id: randomUuid(),
  type,
  x: 0.5,
  y: 0.5,
  scale: 1,
  rotation: 0,
});

const constrainLayer = (layer) => {
  const scaleLimits =
    MESSENGER_V2_IMAGE_LAYER_SCALE_LIMITS[layer.type] ||
    MESSENGER_V2_IMAGE_LAYER_SCALE_LIMITS.default;
  const scale = clamp(layer.scale, scaleLimits.minimum, scaleLimits.maximum);
  const emojiMargin =
    layer.type === 'emoji' ? clamp((Number(layer.size) || 0.16) * scale * 0.5, 0, 0.48) : 0;
  return {
    ...layer,
    x: clamp(layer.x, emojiMargin, 1 - emojiMargin),
    y: clamp(layer.y, emojiMargin, 1 - emojiMargin),
    scale,
    rotation: Number(layer.rotation) || 0,
  };
};

export const addMessengerV2ImageTextLayer = (document, values = {}) => {
  const layer = {
    ...createLayerBase('text'),
    text: '',
    color: DEFAULT_MESSENGER_V2_IMAGE_EDIT_COLOR,
    background: DEFAULT_MESSENGER_V2_IMAGE_TEXT_BACKGROUND,
    size: 0.075,
    maxWidth: MESSENGER_V2_IMAGE_TEXT_MAX_WIDTH,
    ...values,
  };
  return {
    document: updateMessengerV2ImageEditDocument(document, (present) => ({
      ...present,
      layers: [...present.layers, layer],
    })),
    layer,
  };
};

export const ensureMessengerV2ImageTextLayer = (document, layerId) => {
  const current = resolveMessengerV2ImageEditDocument(document);
  const normalizedLayerId = String(layerId || '').trim();
  if (!normalizedLayerId || normalizedLayerId === 'new') return current;
  const existingLayer = current.present.layers.find(
    (layer) => layer.type === 'text' && String(layer.id) === normalizedLayerId,
  );
  if (existingLayer) {
    return setMessengerV2ImageToolSessionLayer(current, existingLayer.id);
  }
  const sessionDocument = beginMessengerV2ImageToolSession(current, 'text', normalizedLayerId);
  const result = addMessengerV2ImageTextLayer(sessionDocument, {
    id: normalizedLayerId,
  });
  return setMessengerV2ImageToolSessionLayer(result.document, result.layer.id);
};

export const addMessengerV2ImageEmojiLayer = (document, emoji, values = {}) => {
  const layer = {
    ...createLayerBase('emoji'),
    emoji: String(emoji || '🙂'),
    size: 0.16,
    ...values,
  };
  return {
    document: updateMessengerV2ImageEditDocument(document, (present) => ({
      ...present,
      layers: [...present.layers, layer],
    })),
    layer,
  };
};

export const addMessengerV2ImageStroke = (document, points = [], values = {}) => {
  const normalizedPoints = points
    .map((point) => ({
      x: clamp(point?.x, 0, 1),
      y: clamp(point?.y, 0, 1),
    }))
    .filter((point) => Number.isFinite(point.x) && Number.isFinite(point.y));
  if (normalizedPoints.length < 2) return resolveMessengerV2ImageEditDocument(document);
  const current = resolveMessengerV2ImageEditDocument(document);
  const layer = {
    id: randomUuid(),
    type: 'stroke',
    color: values.color || current.present.brush.color,
    width: clamp(values.width || current.present.brush.width, 0.002, 0.08),
    points: normalizedPoints,
  };
  return updateMessengerV2ImageEditDocument(current, (present) => ({
    ...present,
    layers: [...present.layers, layer],
  }));
};

export const updateMessengerV2ImageLayer = (document, layerId, changes) =>
  updateMessengerV2ImageEditDocument(document, (present) => {
    const nextLayers = present.layers.map((layer) => {
      if (String(layer.id) !== String(layerId)) return layer;
      const resolvedChanges = typeof changes === 'function' ? changes(layer) : changes;
      return constrainLayer({
        ...layer,
        ...(resolvedChanges || {}),
      });
    });
    return nextLayers.some((layer, index) => layer !== present.layers[index])
      ? { ...present, layers: nextLayers }
      : present;
  });

export const replaceMessengerV2ImageLayer = (document, layerId, changes) =>
  replaceMessengerV2ImageEditPresent(document, (present) => ({
    ...present,
    layers: present.layers.map((layer) =>
      String(layer.id) === String(layerId)
        ? constrainLayer({
            ...layer,
            ...(typeof changes === 'function' ? changes(layer) : changes),
          })
        : layer,
    ),
  }));

export const moveMessengerV2ImageLayer = (document, layerId, position) =>
  replaceMessengerV2ImageEditPresent(document, (present) => ({
    ...present,
    layers: present.layers.map((layer) =>
      String(layer.id) === String(layerId)
        ? {
            ...layer,
            x: clamp(position?.x, -10, 11),
            y: clamp(position?.y, -10, 11),
          }
        : layer,
    ),
  }));

export const discardMessengerV2ImageLayer = (document, layerId) =>
  replaceMessengerV2ImageEditPresent(document, (present) => ({
    ...present,
    layers: present.layers.filter((layer) => String(layer.id) !== String(layerId)),
  }));

export const removeMessengerV2ImageLayer = (document, layerId) =>
  updateMessengerV2ImageEditDocument(document, (present) => ({
    ...present,
    layers: present.layers.filter((layer) => String(layer.id) !== String(layerId)),
  }));

export const setMessengerV2ImageBrush = (document, changes = {}) =>
  updateMessengerV2ImageEditDocument(document, (present) => ({
    ...present,
    brush: {
      color: changes.color || present.brush.color,
      width: clamp(changes.width ?? present.brush.width, 0.002, 0.08),
    },
  }));

export const hasMessengerV2ImageEdits = (document = null) => {
  const current = resolveMessengerV2ImageEditDocument(document);
  const { crop, layers } = current.present;
  return (
    layers.length > 0 ||
    crop.zoom !== 1 ||
    crop.rotation !== 0 ||
    crop.area.x !== 0 ||
    crop.area.y !== 0 ||
    crop.area.width !== 100 ||
    crop.area.height !== 100
  );
};
